import { useEffect, useRef } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { listenBookingKeys } from '../../../../firebase/school-api'
import { useDispatch, useSelector } from 'react-redux'
import {
    makeEntity,
    removeSchoolBookingKeysByDate,
    resetSchoolBookings,
    upsertSchoolBookingKeys,
} from '../../../../redux/schoolBookingsSlice'
import dayjs from '../../../../helpers/dayjs'
import { DataSnapshot, Unsubscribe } from 'firebase/database'
import { SchoolBookingKeysForDate } from '../../../../firebase/types-bookings'
import { reverseDate } from './helper'
import { selectCalendar } from '../../../../redux/calenderSlice'
import {
    makeSchoolReplacementEntity,
    removeSchoolReplacement,
    resetSchoolReplacements,
    SchoolReplacementRequest,
    upsertSchoolReplacement,
} from '../../../../redux/schoolReplacementsSlice'
import { closeAllModals } from '../../../../redux/schoolModalSlice'
import { selectSchoolConfig } from '../../../../redux/schoolSlice'
import { OPPORTUNITIES_API } from '../../../../firebase/opportunities-api'
import { logger } from '../../../../helpers/logger'

/**
 * Entry point/layout for Bookings page
 */
const BookingsPageLayout = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const config = useSelector(selectSchoolConfig)
    const dates = useSelector(selectCalendar)

    // useEffect(() => {
    //     if (!!dates) dispatch(setCalendar(dates))
    // }, [dates, dispatch])

    const currentSchoolUid = useRef<string | null>(null)

    const schoolId = config.data?.schoolId
    const country = config.data?.country
    const locationState = config.data?.locationState
    const currentTerm = dates?.current?.term

    // Attach listener to this year's school keys
    useEffect(() => {
        // DEPS log
        if (
            !schoolId ||
            !country ||
            !locationState ||
            currentTerm === undefined
        ) {
            return
        }

        if (
            currentSchoolUid.current !== null &&
            schoolId !== currentSchoolUid.current
        ) {
            logger.debug(`Close all modals`)
            dispatch(closeAllModals())
            navigate(0)
        }
        // Set currentUID
        currentSchoolUid.current = schoolId

        logger.debug(
            `ATTACH-LISTENER: BookingKeys ${schoolId} ${country} ${locationState}`
        )

        const currentYear = dayjs().tz('Australia/Melbourne')

        const handleKeysUpsert = (
            snap: DataSnapshot,
            reason: 'changed' | 'added'
        ) => {
            const date = snap.key!
            const reversedDate = reverseDate(date)

            const keys = snap.val() as SchoolBookingKeysForDate // {[teacherUid: string]: key}
            dispatch(
                upsertSchoolBookingKeys(
                    Object.entries(keys)
                        .filter(([id]) => id !== 'promptedForReview')
                        .map(([teacherUid, key]) =>
                            makeEntity(reversedDate, teacherUid, key)
                        )
                )
            )
        }

        const handleReplacementUpsert = (snap: DataSnapshot) => {
            const requestUid = snap.key!
            const data = snap.val() as SchoolReplacementRequest
            dispatch(
                upsertSchoolReplacement(
                    makeSchoolReplacementEntity(requestUid, data)
                )
            )
        }

        // Attach current year listener
        const thisYearUnsubs = listenBookingKeys(
            country,
            locationState,
            schoolId,
            currentYear.format('YYYY'),
            {
                added: (snap) => handleKeysUpsert(snap, 'added'),
                changed: (snap) => handleKeysUpsert(snap, 'changed'),
                removed: (snap) => {
                    const date = snap.key!
                    const reversedDate = reverseDate(date)
                    dispatch(removeSchoolBookingKeysByDate(reversedDate))
                },
            }
        )

        // Attach next year listener if term 4
        let nextYearSubs: Unsubscribe[] = []
        if (currentTerm === 'termFour') {
            logger.debug(`Currently T4 - attach next year`)
            nextYearSubs = listenBookingKeys(
                country,
                locationState,
                schoolId,
                currentYear.add(1, 'y').format('YYYY'),
                {
                    added: (snap) => handleKeysUpsert(snap, 'added'),
                    changed: (snap) => handleKeysUpsert(snap, 'changed'),
                    removed: (snap) => {
                        const date = snap.key!

                        dispatch(
                            removeSchoolBookingKeysByDate(reverseDate(date))
                        )
                    },
                }
            )
        }

        // Attach listeners for replacement requests
        const replacementUnsubs = OPPORTUNITIES_API.listenReplacements({
            country,
            locationState,
            schoolUid: schoolId,
            callback: {
                added: handleReplacementUpsert,
                changed: handleReplacementUpsert,
                removed: (snap) => {
                    dispatch(removeSchoolReplacement(snap.key!))
                },
            },
        })

        return () => {
            setTimeout(() => {
                logger.debug('DETACH-LISTENER: bookingKeys')
                thisYearUnsubs.forEach((offFn) => offFn())
                nextYearSubs.forEach((offFn) => offFn())
                replacementUnsubs.forEach((offFn) => offFn())

                dispatch(resetSchoolBookings())
                dispatch(resetSchoolReplacements())
            }, 0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolId, country, locationState, currentTerm, dispatch])

    return <Outlet />
}

export default BookingsPageLayout
